import React, { forwardRef } from "react";
import styled from "@emotion/styled";

const Row = styled.div(
  ({ theme }) => `
  display: flex;
  min-height: 56px;
  max-height: 72px;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.06)" : theme.divider
  };
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover,
  &:focus {
    background: ${theme.hover};
    outline: none;
  }

  &:focus {
    box-shadow: inset 0 0 0 2px ${theme.primary}80;
  }
`
);

const RowIcon = styled.div(
  ({ theme }) => `
  display: flex;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: ${theme.text.disabled};
`
);

const RowBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const RowTitle = styled.span(
  ({ theme }) => `
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: ${theme.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
);

const RowMeta = styled.span(
  ({ theme }) => `
  flex-shrink: 0;
  font-size: 11px;
  color: ${theme.text.disabled};
`
);

const RowSubtitle = styled.p(
  ({ theme }) => `
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${theme.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
);

const ExploreButton = styled.button(
  ({ theme }) => `
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid ${
    theme.type === "dark" ? "rgba(255,255,255,0.15)" : theme.divider
  };
  background: transparent;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  color: ${theme.text.secondary};
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${
      theme.type === "dark" ? "rgba(255,255,255,0.25)" : theme.borderColor
    };
    color: ${theme.text.primary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.primary}66;
  }
`
);

const SearchResultRow = forwardRef(function SearchResultRow(
  {
    icon,
    title,
    subtitle,
    metadata,
    onOpen,
    onExplore,
    focused = false,
    onKeyDown,
    "aria-posinset": ariaPosInSet,
    "aria-setsize": ariaSetSize,
  },
  ref
) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onExplore) {
      e.preventDefault();
      onExplore();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onOpen();
      return;
    }
    onKeyDown?.(e);
  };

  return (
    <Row
      ref={ref}
      role="option"
      tabIndex={focused ? 0 : -1}
      aria-posinset={ariaPosInSet}
      aria-setsize={ariaSetSize}
      onClick={(e) => {
        if (e.target.closest("[data-secondary-action]")) return;
        onOpen();
      }}
      onKeyDown={handleKeyDown}
    >
      <RowIcon aria-hidden>{icon}</RowIcon>
      <RowBody>
        <TitleRow>
          <RowTitle>{title}</RowTitle>
          {metadata && <RowMeta>{metadata}</RowMeta>}
        </TitleRow>
        {subtitle && <RowSubtitle>{subtitle}</RowSubtitle>}
      </RowBody>
      {onExplore && (
        <ExploreButton
          type="button"
          data-secondary-action
          onClick={(e) => {
            e.stopPropagation();
            onExplore();
          }}
          aria-label="Explore in Data Explorer"
        >
          Explore
        </ExploreButton>
      )}
    </Row>
  );
});

export default SearchResultRow;
